import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  // Apelidado: `aoClicarFora`, mais abaixo, é um ouvinte de `document` e recebe o
  // `MouseEvent` **do DOM**. Importar o do React com o mesmo nome sombrearia o
  // global e trocaria o tipo do ouvinte sem que nada no código mudasse.
  type MouseEvent as MouseEventReact,
  type ReactNode,
} from 'react'

import { cn } from '../utilitarios/cn'

export interface ItemMenu {
  rotulo: string
  onSelecionar: () => void
  icone?: ReactNode
  desabilitado?: boolean
  /** Motivo do bloqueio — vira `title`, para o usuário saber por que não pode. */
  motivoBloqueio?: string
  destrutivo?: boolean
}

export interface PropriedadesMenu {
  /**
   * O controle que abre o menu. **Passe um elemento focável** — `<Botao>`, ou um
   * `<button>` — e o `Menu` acopla nele `onClick`, `aria-haspopup` e
   * `aria-expanded`, preservando o `onClick` que ele já tiver.
   *
   * Um nó não-elemento (texto, fragmento) continua funcionando: o `Menu` o embrulha
   * num `<button>` próprio. O que não existe mais é a possibilidade de o gatilho
   * ficar inalcançável pelo teclado — era o CI-2 de `docs/release/13-ci.md §7`.
   */
  gatilho: ReactNode
  itens: ReadonlyArray<ItemMenu>
  alinhamento?: 'esquerda' | 'direita'
  className?: string
}

/**
 * Menu suspenso — o botão `Ações` da TELA-04 e o menu do usuário na topbar.
 *
 * Item sem permissão fica **desabilitado com motivo**, não escondido, quando a
 * ação existe para outros papéis: desaparecer sem explicação faz o usuário
 * procurar o botão de novo em cada visita. Quando o recurso inteiro é invisível
 * para o papel, quem chama simplesmente não passa o item.
 */
export function Menu({ gatilho, itens, alinhamento = 'direita', className }: PropriedadesMenu) {
  const [aberto, setAberto] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberto) return
    function aoClicarFora(evento: MouseEvent) {
      if (!container.current?.contains(evento.target as Node)) setAberto(false)
    }
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') setAberto(false)
    }
    document.addEventListener('mousedown', aoClicarFora)
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('mousedown', aoClicarFora)
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [aberto])

  /**
   * CORRIGE O CI-2 de `docs/release/13-ci.md §7` (WCAG 2.1.1 e 4.1.2, RNF-113).
   *
   * Antes, o gatilho vinha embrulhado num `<div onClick>`. Duas consequências, e a
   * segunda vale mais que a primeira:
   *
   *  1. Gatilho **não focável** — um `gatilho` que não fosse por si um controle
   *     (texto, `<span>`, ícone solto) ficava inalcançável pelo teclado. Os três
   *     pontos de chamada de hoje passam `<Botao>` ou `<button>`, e neles o teclado
   *     funcionava por borbulhamento do `click`; o contrato do componente é que
   *     permitia o defeito, e contrato assim cobra em silêncio na próxima tela.
   *  2. **Sem estado anunciado** — nada dizia ao leitor de tela que aquele controle
   *     abre um menu, nem se ele está aberto. `aria-haspopup` e `aria-expanded` não
   *     existiam em lugar nenhum.
   *
   * A correção não embrulha em `<button>` por cima: os três gatilhos já SÃO botões,
   * e botão dentro de botão é HTML inválido e dois pontos de tabulação. Em vez
   * disso, os atributos são acoplados ao próprio elemento recebido — que é onde o
   * leitor de tela vai buscá-los. `onClick` preexistente do gatilho é preservado,
   * não substituído.
   */
  const alternar = () => setAberto((valor) => !valor)

  const atributosDeMenu = { 'aria-haspopup': 'menu', 'aria-expanded': aberto } as const
  const elementoGatilho = isValidElement<HTMLAttributes<HTMLElement>>(gatilho) ? gatilho : null

  const gatilhoAcessivel =
    elementoGatilho === null ? (
      // Nó que não é elemento: um `<button>` sem estilo próprio, só para dar ao
      // conteúdo um papel, um foco e o teclado.
      <button
        type="button"
        onClick={alternar}
        {...atributosDeMenu}
        className="cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marca-500/45"
      >
        {gatilho}
      </button>
    ) : (
      cloneElement(elementoGatilho, {
        ...atributosDeMenu,
        onClick: (evento: MouseEventReact<HTMLElement>) => {
          elementoGatilho.props.onClick?.(evento)
          alternar()
        },
      })
    )

  return (
    <div ref={container} className={cn('relative', className)}>
      {gatilhoAcessivel}
      {aberto && (
        <div
          role="menu"
          className={cn(
            'absolute z-40 mt-1 min-w-52 rounded-md border border-borda bg-superficie p-1 shadow-lg',
            alinhamento === 'direita' ? 'right-0' : 'left-0',
          )}
        >
          {itens.map((item) => (
            <button
              key={item.rotulo}
              role="menuitem"
              type="button"
              disabled={item.desabilitado}
              title={item.desabilitado ? item.motivoBloqueio : undefined}
              onClick={() => {
                setAberto(false)
                item.onSelecionar()
              }}
              className={cn(
                'flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-left text-sm transition-colors',
                item.destrutivo ? 'text-perigo' : 'text-conteudo',
                item.desabilitado
                  ? 'cursor-not-allowed opacity-45'
                  : 'hover:bg-superficie-2',
              )}
            >
              {item.icone ? <span className="shrink-0 text-conteudo-fraco">{item.icone}</span> : null}
              {item.rotulo}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
